import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVolumeUp, faVolumeMute, faSpinner, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';

interface VideoWithSoundProps {
  src: string;
  index: number;
  videoRefs: React.MutableRefObject<(HTMLVideoElement | null)[]>;
  handleVideoClick: (index: number) => void;
  autoPlay?: boolean;
  webmSrc?: string; // Source au format WebM (plus léger)
  poster?: string; // Image poster personnalisée
  driveUrl?: string; // URL vers la version Google Drive de la vidéo
  title?: string; // Titre de la vidéo pour l'accessibilité
}

// Gestionnaire global pour suivre les vidéos visibles
interface VideoVisibilityState {
  [key: string]: boolean;
}

// Singleton pour partager l'état de visibilité entre les composants
class VideoVisibilityManager {
  private static instance: VideoVisibilityManager;
  private visibilityState: VideoVisibilityState = {};
  private observers: Set<(src: string, isVisible: boolean) => void> = new Set();

  private constructor() {}

  public static getInstance(): VideoVisibilityManager {
    if (!VideoVisibilityManager.instance) {
      VideoVisibilityManager.instance = new VideoVisibilityManager();
    }
    return VideoVisibilityManager.instance;
  }

  public setVideoVisibility(src: string, isVisible: boolean): void {
    this.visibilityState[src] = isVisible;
    this.notifyObservers(src, isVisible);
  }

  public isVideoVisible(src: string): boolean {
    return !!this.visibilityState[src];
  }

  public subscribe(callback: (src: string, isVisible: boolean) => void): () => void {
    this.observers.add(callback);
    return () => {
      this.observers.delete(callback);
    };
  }

  private notifyObservers(src: string, isVisible: boolean): void {
    this.observers.forEach(observer => observer(src, isVisible));
  }
}

const VideoWithSound: React.FC<VideoWithSoundProps> = ({ 
  src, 
  index, 
  videoRefs, 
  handleVideoClick,
  autoPlay = false,
  webmSrc,
  poster,
  driveUrl,
  title = "Vidéo"
}) => {
  const [isMuted, setIsMuted] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isFullyLoaded, setIsFullyLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const touchStartTimeRef = useRef<number>(0);
  const touchPositionRef = useRef<{x: number, y: number}>({x: 0, y: 0});
  const visibilityManager = VideoVisibilityManager.getInstance();
  const observerRef = useRef<IntersectionObserver | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  
  // Détecter si l'appareil est mobile
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
      setIsMobile(isMobileDevice);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  
  // Configuration de l'Intersection Observer pour détecter la visibilité
  useEffect(() => {
    if (!containerRef.current) return;
    
    const options = {
      root: null,
      rootMargin: isMobile ? '200px' : '100px', // Précharger un peu avant que la vidéo soit visible
      threshold: isMobile ? 0.1 : 0.2
    };
    
    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        const isNowVisible = entry.isIntersecting;
        setIsVisible(isNowVisible);
        visibilityManager.setVideoVisibility(src, isNowVisible);
        
        // Gérer le chargement intelligent
        if (videoRef.current) {
          if (isNowVisible) {
            // Vidéo visible : charger complètement si pas déjà fait
            if (!isFullyLoaded && isMobile) {
              // Sur mobile, charger progressivement
              videoRef.current.preload = 'auto';
              // Forcer le rechargement pour appliquer le nouveau preload
              if (videoRef.current.readyState < 2) { // Si pas déjà suffisamment chargé
                const currentTime = videoRef.current.currentTime;
                const wasPaused = videoRef.current.paused;
                videoRef.current.load();
                videoRef.current.currentTime = currentTime;
                if (!wasPaused && autoPlay) {
                  videoRef.current.play().catch(() => {});
                }
              }
              setIsFullyLoaded(true);
            }
          } else if (isMobile && !autoPlay) {
            // Vidéo non visible sur mobile : réduire la qualité et libérer des ressources
            if (videoRef.current.readyState > 2) { // Si la vidéo était chargée
              videoRef.current.preload = 'metadata';
              if (!videoRef.current.paused) {
                videoRef.current.pause();
              }
            }
          }
        }
      });
    };
    
    observerRef.current = new IntersectionObserver(handleIntersection, options);
    observerRef.current.observe(containerRef.current);
    
    return () => {
      if (observerRef.current && containerRef.current) {
        observerRef.current.unobserve(containerRef.current);
        observerRef.current.disconnect();
      }
    };
  }, [src, isMobile, autoPlay, isFullyLoaded]);
  
  // Fonction pour activer/désactiver le son
  const toggleMute = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation(); // Empêche le déclenchement du handleVideoClick
    e.preventDefault(); // Empêche les comportements par défaut sur mobile
    
    const video = videoRefs.current[index];
    if (!video) return;
    
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const handleVideoClickLocal = () => {
    if (!videoRef.current) return;
    
    // Utiliser un délai plus long pour éviter les conflits entre play() et pause()
    // Délai plus long sur mobile
    const delay = isMobile ? 200 : 100;
    
    if (videoRef.current.paused) {
      // Ajouter un délai avant de lancer la lecture
      setTimeout(() => {
        if (videoRef.current && videoRef.current.paused) {
          // Sur mobile, forcer le mute pour permettre l'autoplay
          if (isMobile && !videoRef.current.muted) {
            videoRef.current.muted = true;
            setIsMuted(true);
          }
          
          videoRef.current.play().catch(error => {
            // Gérer les erreurs spécifiques aux mobiles
            if (error.name === 'NotAllowedError') {
              console.log('Autoplay bloqué sur mobile, tentative avec mute');
              if (videoRef.current) {
                videoRef.current.muted = true;
                setIsMuted(true);
                videoRef.current.play().catch(err => {
                  console.error('Échec de lecture même avec mute:', err);
                });
              }
            } else if (error.name !== 'AbortError') {
              console.error("Erreur lors de la lecture de la vidéo:", error);
            }
          });
        }
      }, delay);
    } else {
      videoRef.current.pause();
    }
  };
  
  // Gestionnaires d'événements tactiles
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartTimeRef.current = Date.now();
    if (e.touches.length > 0) {
      touchPositionRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY
      };
    }
  };
  
  const handleTouchEnd = (e: React.TouchEvent) => {
    // Vérifier si c'est un tap simple (pas un swipe ou un zoom)
    const touchDuration = Date.now() - touchStartTimeRef.current;
    
    if (e.changedTouches.length > 0) {
      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;
      const deltaX = Math.abs(touchEndX - touchPositionRef.current.x);
      const deltaY = Math.abs(touchEndY - touchPositionRef.current.y);
      
      // Si c'est un tap simple (pas un swipe) et de courte durée
      if (touchDuration < 300 && deltaX < 20 && deltaY < 20) {
        e.preventDefault(); // Empêcher le zoom ou d'autres comportements par défaut
        handleVideoClickLocal();
        handleVideoClick(index);
      }
    }
  };

  // Utiliser un poster personnalisé ou générer un à partir du nom de fichier
  const posterSrc = poster || src.replace(/\.[^/.]+$/, ".jpg");
  
  // Fonction pour charger la vidéo en basse qualité d'abord sur mobile
  const handleLoadStart = () => {
    if (videoRef.current) {
      // Configuration de base pour tous les appareils
      videoRef.current.setAttribute('playsinline', '');
      
      if (isMobile) {
        // Stratégie de chargement intelligent sur mobile
        if (!isVisible) {
          // Si la vidéo n'est pas visible, charger uniquement les métadonnées
          videoRef.current.preload = 'metadata';
          videoRef.current.dataset.loadState = 'metadata-only';
        } else if (autoPlay) {
          // Si la vidéo est visible et en autoplay, charger complètement
          videoRef.current.preload = 'auto';
          videoRef.current.dataset.loadState = 'loading-full';
          setIsFullyLoaded(true);
        } else {
          // Si la vidéo est visible mais pas en autoplay, charger progressivement
          videoRef.current.preload = 'metadata';
          videoRef.current.dataset.loadState = 'progressive';
          
          // Charger complètement après un court délai si toujours visible
          setTimeout(() => {
            if (isVisible && videoRef.current && videoRef.current.dataset.loadState === 'progressive') {
              videoRef.current.preload = 'auto';
              videoRef.current.dataset.loadState = 'loading-full';
              setIsFullyLoaded(true);
            }
          }, 1000);
        }
        
        // Optimisation de la qualité pour les appareils mobiles
        if (videoRef.current.canPlayType('video/webm')) {
          videoRef.current.style.objectFit = 'cover';
        }
      } else {
        // Sur desktop, charger normalement mais de manière plus agressive
        videoRef.current.preload = isVisible ? 'auto' : 'metadata';
      }
    }
  };
  
  // Gérer les changements de visibilité pour optimiser le chargement
  useEffect(() => {
    if (!videoRef.current || !isMobile) return;
    
    if (isVisible) {
      // Vidéo visible : augmenter progressivement la qualité
      if (videoRef.current.preload !== 'auto' && !isFullyLoaded) {
        // Passer en mode de chargement complet après un court délai
        setTimeout(() => {
          if (videoRef.current && isVisible) {
            videoRef.current.preload = 'auto';
            videoRef.current.dataset.loadState = 'loading-full';
            setIsFullyLoaded(true);
          }
        }, 500);
      }
    } else {
      // Vidéo non visible : libérer des ressources si nécessaire
      if (!autoPlay && videoRef.current.preload === 'auto') {
        videoRef.current.preload = 'metadata';
        
        // Si la vidéo était en cours de lecture, la mettre en pause
        if (!videoRef.current.paused) {
          videoRef.current.pause();
        }
      }
    }
  }, [isVisible, isMobile, autoPlay, isFullyLoaded]);

  // État pour suivre si la vidéo est en cours de chargement
  const [isLoading, setIsLoading] = useState(true);
  // État pour stocker les dimensions de la vidéo
  const [videoDimensions, setVideoDimensions] = useState({ width: 0, height: 0, aspectRatio: 16/9 });
  
  // Effet pour détecter les dimensions de la vidéo
  useEffect(() => {
    // Précharger l'image poster pour obtenir les dimensions
    if (posterSrc) {
      const img = new Image();
      img.onload = () => {
        const aspectRatio = img.width / img.height;
        setVideoDimensions({
          width: img.width,
          height: img.height,
          aspectRatio
        });
      };
      img.src = posterSrc;
    }
  }, [posterSrc]);

  return (
    <div className="relative" ref={containerRef}>
      {/* Conteneur qui maintient le ratio d'aspect de la vidéo */}
      <div 
        className="relative w-full overflow-hidden" 
        style={{ paddingBottom: `${(1 / videoDimensions.aspectRatio) * 100}%` }}
      >
        {/* Spinner de chargement */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-50 z-10">
            <div className="text-center">
              <FontAwesomeIcon 
                icon={faSpinner} 
                className="animate-spin text-gray-700" 
                size="3x" 
              />
            </div>
          </div>
        )}
        
        {/* Afficher un poster de haute qualité pendant le chargement */}
        {!isFullyLoaded && (
          <div 
            className="absolute inset-0 bg-cover bg-center z-0" 
            style={{ backgroundImage: `url(${posterSrc})`, opacity: isVisible ? 0.7 : 1 }}
          />
        )}
        
        <video 
          ref={el => {
            videoRefs.current[index] = el;
            videoRef.current = el;
          }}
          className={`absolute inset-0 w-full h-full object-cover cursor-pointer ${isLoading ? 'opacity-0' : 'opacity-100'}`}
          onClick={() => {
            handleVideoClickLocal();
            handleVideoClick(index);
          }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onLoadStart={handleLoadStart}
          onLoadedData={() => {
            // Marquer la vidéo comme chargée
            setIsLoading(false);
            setIsFullyLoaded(true);
            
            // Mettre à jour les dimensions si nécessaire
            if (videoRef.current) {
              const aspectRatio = videoRef.current.videoWidth / videoRef.current.videoHeight;
              setVideoDimensions({
                width: videoRef.current.videoWidth,
                height: videoRef.current.videoHeight,
                aspectRatio
              });
            }
          }}
          onError={() => {
            // En cas d'erreur, masquer le spinner
            setIsLoading(false);
          }}
          loop
          muted={isMuted}
          playsInline
          preload={isVisible || !isMobile ? "auto" : "metadata"}
          autoPlay={autoPlay && isVisible}
          poster={posterSrc}
          data-index={index}
          data-visible={isVisible}
        >
          {/* Utiliser des sources multiples pour différents formats */}
          {webmSrc && <source src={webmSrc} type="video/webm" />}
          <source 
            src={src} 
            type={src.endsWith('.mp4') ? "video/mp4" : src.endsWith('.m4v') ? "video/mp4" : "video/mp4"} 
          />
        </video>
      </div>
      {/* Bouton pour activer/désactiver le son */}
      <button 
        className="absolute bottom-3 right-3 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 focus:outline-none z-10"
        onClick={toggleMute}
        onTouchEnd={(e) => toggleMute(e)}
        aria-label={isMuted ? "Activer le son" : "Désactiver le son"}
      >
        <FontAwesomeIcon icon={isMuted ? faVolumeMute : faVolumeUp} />
      </button>
      
      {/* Lien vers Google Drive avec icône plein écran */}
      {driveUrl && (
        <a 
          href={driveUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="absolute bottom-3 left-3 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 focus:outline-none z-10 transition-all duration-300 hover:bg-black"
          title={`Voir ${title} en haute qualité sur Google Drive`}
        >
          <FontAwesomeIcon icon={faExternalLinkAlt} size="sm" />
        </a>
      )}
    </div>
  );
};

export default VideoWithSound;
